import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormFieldLayout,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { NameDto } from 'api/Common/Types'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Link } from 'components/Link'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { links } from 'utils/featureToggles'

export enum ConversionDirection {
  From = 'from',
  To = 'to',
}

interface CreateConversionFormValues {
  from: SelectableOptionField | null
  to: SelectableOptionField | null
  amountFrom: number | string | null
  amountTo: number | string | null
  minAmount: number
  direction: ConversionDirection
  client: string | null
  comment: string
}

interface SelectableOptionField {
  id: string
  name: string
  currency: CurrencyType
  balance: number
}

export interface CreateConversionFormSubmitValues {
  fromId: string
  toId: string
  fromCurrencyId?: CurrencyType
  toCurrencyId?: CurrencyType
  fromAmount: number
  toAmount: number
  direction: ConversionDirection
  comment: string
}

export interface PresetAccount {
  account?: TradingAccountDto
  currency?: CurrencyType
}

const useConversionOptions = (props: OuterProps) => {
  const getTradingAccounts = useMemo(() => {
    const tradingAccount = props.tradingAccounts.find(
      (ta) => ta?.platformOverview?.balances?.length > 0
    )

    if (!tradingAccount) return []

    const balances = tradingAccount.platformOverview.balances

    return balances.map<SelectableOptionField>((balance) => ({
      id: tradingAccount.id,
      name: tradingAccount.name,
      currency: balance.currency,
      balance: balance.balance,
    }))
  }, [props.tradingAccounts])

  const fromOptions = useMemo(() => {
    return [...getTradingAccounts]
  }, [getTradingAccounts])

  const calculateAmountByExchangeRate = (amount: number, exchangeRate: number) => {
    return amount * exchangeRate
  }

  const calculateAmountByReverseExchangeRate = (amount: number, exchangeRate: number) => {
    return amount / exchangeRate
  }

  const toOptions = useMemo(() => {
    const tradingAccount = props.tradingAccounts.find(
      (ta) => ta?.platformOverview?.balances?.length > 0
    )

    return props.availableTACurrencies.map(
      (currency) =>
        getTradingAccounts.find((item) => item.currency === currency.id) || {
          id: currency.id,
          currency: currency.id,
          balance: 0,
          name: tradingAccount?.name || '',
        }
    )
  }, [getTradingAccounts, props.availableTACurrencies, props.tradingAccounts])

  const calculateAmount = useCallback(
    (amount: number | string | null, reverse?: boolean) => {
      const numericAmount = toNumber(amount)

      if (!numericAmount) {
        return 0
      }

      return reverse
        ? calculateAmountByReverseExchangeRate(numericAmount, props.exchangeRate)
        : calculateAmountByExchangeRate(numericAmount, props.exchangeRate)
    },
    [props.exchangeRate]
  )

  return {
    fromOptions,
    toOptions,
    calculateAmount,
  }
}

const ConversionFormUI: React.FC<
  FormikProps<CreateConversionFormValues> & OuterProps & ConversionFormContainerProps
> = (props) => {
  const { values, errors, isSubmitting, client, setFieldValue, onSubmitForm } = props

  const { calculateAmount, fromOptions, toOptions } = useConversionOptions(props)
  useFetchRate(props, values)

  const availableToOptions = useMemo(
    () => toOptions.filter((option) => values.from?.currency !== option.currency),
    [toOptions, values.from?.currency]
  )

  useEffect(() => {
    if (!availableToOptions.length || !values.to) {
      return
    }

    if (!availableToOptions.some((option) => option.currency === values.to?.currency)) {
      setFieldValue('to', null)
    }
  }, [availableToOptions, setFieldValue, values.to])

  useEffect(() => {
    if (values.direction === ConversionDirection.From) {
      setFieldValue('amountTo', calculateAmount(values.amountFrom).toFixed(2))
    } else {
      setFieldValue('amountFrom', calculateAmount(values.amountTo, true).toFixed(2))
    }
  }, [
    calculateAmount,
    props.exchangeRate,
    setFieldValue,
    values.amountFrom,
    values.amountTo,
    values.direction,
  ])

  const exchangeRateValue =
    values.from?.currency && values.to?.currency
      ? props.exchangeRate > 0
        ? values.direction === ConversionDirection.From
          ? props.exchangeRate
          : (1 / props.exchangeRate).toFixed(2)
        : ''
      : ''

  const fromTradingAccountRightAddon = values.from ? (
    <Link
      to={generatePath(links.recordsTradingAccounts.detailsUrl as string, {
        id: values.from.id,
        slug: 'details',
      })}
      showIcon
    />
  ) : undefined

  const toTradingAccountRightAddon =
    values.from && values.to ? (
      <Link
        to={generatePath(links.recordsTradingAccounts.detailsUrl as string, {
          id: values.from.id,
          slug: 'details',
        })}
        showIcon
      />
    ) : undefined

  return (
    <Form onSubmit={onSubmitForm} data-test='transaction-form'>
      <DataGrid columns={1}>
        <DataColumn>
          <DataCard title='Conversion' columns={2}>
            <FormReadonlyField
              name='client'
              label='Client'
              value={values.client ?? ''}
              dataTest='client-field'
              rightAddon={
                <>
                  {props.setClient && (
                    <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                      Select
                    </Button>
                  )}
                  {client?.id && (
                    <Link
                      to={generatePath(links.recordsClients.detailsUrl as string, {
                        id: client.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )}
                </>
              }
            />
            <FormSelectField
              name='from'
              label='Debit Trading Account'
              value={values.from?.currency}
              options={fromOptions.map((option) => ({
                id: option.currency,
                name: `${option.name}-${option.currency}-balance:${option.balance}`,
              }))}
              error={errors.from}
              showSearch
              onChange={(value) => {
                const nextFrom = fromOptions.find((option) => option.currency === value) ?? null
                setFieldValue('from', nextFrom)
                setFieldValue('to', null)
              }}
              rightAddon={fromTradingAccountRightAddon}
            />
            <FormSelectField
              name='to'
              label='Credit Trading Account'
              value={values.to?.currency}
              options={availableToOptions.map((option) => ({
                id: option.currency,
                name: `${option.name}-${option.currency}-balance:${option.balance}`,
              }))}
              error={errors.to}
              showSearch
              onChange={(value) => {
                const nextTo =
                  availableToOptions.find((option) => option.currency === value) ?? null
                setFieldValue('to', nextTo)
              }}
              rightAddon={toTradingAccountRightAddon}
            />
            <FormReadonlyField label='Exchange Rate' value={exchangeRateValue} />
            <FormFieldLayout name='amountFrom' label='Debit amount' error={errors.amountFrom}>
              <Input
                id='amountFrom'
                name='amountFrom'
                min='0'
                type='number'
                step={0.01}
                value={values.amountFrom ?? ''}
                disabled={!props.exchangeRate || props.rateLoading}
                onChange={(event) => {
                  setFieldValue('direction', ConversionDirection.From)
                  setFieldValue('amountFrom', Number(event.target.value))
                }}
                data-test='amount-field'
              />
              {values.from?.currency && (
                <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                  {values.from.currency}
                </span>
              )}
            </FormFieldLayout>
            <FormFieldLayout name='amountTo' label='Credit amount' error={errors.amountTo}>
              <Input
                id='amountTo'
                name='amountTo'
                min='0'
                type='number'
                step={0.01}
                value={values.amountTo ?? ''}
                disabled={!props.exchangeRate || props.rateLoading}
                onChange={(event) => {
                  setFieldValue('direction', ConversionDirection.To)
                  setFieldValue('amountTo', Number(event.target.value))
                }}
                data-test='amount-field'
              />
              {values.to?.currency && (
                <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                  {values.to.currency}
                </span>
              )}
            </FormFieldLayout>
            <FormTextareaField
              name='comment'
              label='Comment'
              rows={3}
              maxLength={130}
              dataTest='comment-field'
            />
          </DataCard>
        </DataColumn>
      </DataGrid>
      <div className='mt-4 flex justify-center'>
        <Button disabled={isSubmitting} key='submit' data-test='submit' type='submit'>
          Submit
        </Button>
      </div>
    </Form>
  )
}

interface ConversionFormContainerProps {
  onSubmitForm: (event: React.FormEvent<HTMLFormElement>) => void
}

const ConversionFormContainer: React.FC<FormikProps<CreateConversionFormValues> & OuterProps> = (
  props
) => {
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = await props.validateForm(props.values)

    if (Object.keys(nextErrors).length === 0) {
      handleConfirmationOpenModal()
    }
  }

  const handleConfirmationSubmit = async () => {
    try {
      await props.submitForm()
      handleConfirmationCloseModal()
    } catch (e) {
      console.error(e)
      handleConfirmationCloseModal()
    }

    props.resetForm()
  }

  const handleConfirmationOpenModal = () => {
    setConfirmationModalOpen(true)
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModalOpen(false)
  }

  const { values } = props

  return (
    <>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to Convert fund from the Trading Account
                {values.from?.name} to the Trading Account {values.to?.name}. The parameters
                selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {props?.client?.name}
                </div>
                <div id='field'>
                  <strong>From Trading Account:</strong> {values.from?.name}
                </div>
                <div id='field'>
                  <strong>To Trading Account:</strong> {values.to?.name}
                </div>
                <div id='field'>
                  <strong>From Amount:</strong> {values.amountFrom} {values.from?.currency}
                </div>
                <div id='field'>
                  <strong>To Amount:</strong> {props.values.amountTo} {values.to?.currency}
                </div>
              </div>
            </div>
          }
        />
      )}
      <ConversionFormUI {...props} onSubmitForm={handleSubmitForm} />
    </>
  )
}

export type FormSubmitValues<T> = {
  [K in keyof T]: T[K] | null
}

interface OuterProps {
  client: ClientDto | null
  setClient?: () => void
  tradingAccounts: TradingAccountDto[]
  availableTACurrencies: NameDto<CurrencyType>[]
  fromAccount?: PresetAccount
  fetchRate(currencyFrom: CurrencyType, currencyTo: CurrencyType): void
  exchangeRate: number
  rateLoading: boolean
  onSubmit(values: FormSubmitValues<CreateConversionFormSubmitValues>): Promise<void>
}

const useFetchRate = (props: OuterProps, values: CreateConversionFormValues) => {
  const { fetchRate } = props

  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout

    if (values.from?.currency && values.to?.currency) {
      delayDebounceFn = setTimeout(() => {
        fetchRate(values.from?.currency as CurrencyType, values.to?.currency as CurrencyType)
      }, 1000)
    }

    return () => clearTimeout(delayDebounceFn)
  }, [fetchRate, values.from, values.to])
}

function dataToSubmit(
  values: CreateConversionFormValues
): FormSubmitValues<CreateConversionFormSubmitValues> {
  return {
    fromId: values.from?.id || '',
    toId: values.to?.id || '',
    direction: values.direction || ConversionDirection.From,
    fromCurrencyId: values?.from?.currency,
    toCurrencyId: values?.to?.currency,
    fromAmount: Number.parseFloat((values.amountFrom || 0).toString()),
    toAmount: Number.parseFloat((values.amountTo || 0).toString()),
    comment: values.comment,
  }
}

export default withFormik<OuterProps, CreateConversionFormValues>({
  mapPropsToValues: ({ fromAccount, client }) => {
    const minAmount = 0.01
    const baseValues: CreateConversionFormValues = {
      client: client?.name ?? null,
      from: null,
      to: null,
      amountFrom: null,
      amountTo: null,
      minAmount,
      direction: ConversionDirection.From,
      comment: '',
    }

    if (fromAccount?.account && fromAccount?.currency) {
      const balances = fromAccount.account.platformOverview.balances
      const selectedBalance = balances.find((item) => item.currency === fromAccount.currency)
      const balance = selectedBalance?.balance || 0

      return {
        ...baseValues,
        from: {
          id: fromAccount.account.id,
          name: fromAccount.account.name,
          currency: fromAccount.currency,
          balance,
        },
      }
    }

    return baseValues
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<CreateConversionFormValues> = {}
    const amountFrom = toNumber(values.amountFrom)
    const amountTo = toNumber(values.amountTo)

    if (!values.from) errors.from = 'Required'
    if (!values.to) errors.to = 'Required'
    if (!amountFrom) errors.amountFrom = 'Required'
    if (!amountTo) errors.amountTo = 'Required'

    if (amountFrom && amountFrom < values.minAmount) {
      errors.amountFrom = `Minimum amount is ${values?.from?.currency} ${values.minAmount}`
    }

    if (amountFrom > 0 && amountTo < 0.01) {
      errors.amountTo = `Minimum amount is ${values?.to?.currency} 0.01`
    }

    if (amountFrom && values.from?.balance && amountFrom > values.from.balance) {
      errors.amountFrom = 'There is not enough balance in your trading account'
    }

    return errors
  },
})(ConversionFormContainer)
